import { type UserCredential } from '@linktivity/link-utils';
import { WebMixerService } from '@linktivity/triplabo-taxfree';
import i18n, { DEFAULT_LOCALE, resetLocale } from '@triplabo/web/i18n';
import { config as env, isWechat } from '@triplabo/web/utils/env';
import { getWebAuth } from '@triplabo/web/vendors/firebase';

const EMAIL_FOR_SIGN_IN_KEY = 'email_for_sign_in';

// mini program users never sign in to Firebase; touching it there would load its iframe
const signOutFirebase = async () => {
  if (isWechat) return;
  await getWebAuth().logout();
};

export const AUTH_HEADER = 'grpc-metadata-authorization';
export const LANG_HEADER = 'Language';

export const langMiddleware: WebMixerService.Middleware = {
  pre: async ({ url, init }) => {
    const headers = new Headers(init.headers);
    headers.set(LANG_HEADER, (i18n.language || DEFAULT_LOCALE).toUpperCase());

    return { url, init: { ...init, headers } };
  }
};

export const accountApi = new WebMixerService.AccountApi(
  new WebMixerService.Configuration({
    basePath: env.apiBase,
    middleware: [langMiddleware]
  })
);

const createTokenStore = (
  accessKey: string,
  refreshKey: string,
  onExpired?: () => void
) => {
  const getAccessToken = () => localStorage.getItem(accessKey) ?? '';

  const getRefreshToken = () => localStorage.getItem(refreshKey) ?? '';

  const setTokens = (accessToken: string, refreshToken: string) => {
    localStorage.setItem(accessKey, accessToken);
    localStorage.setItem(refreshKey, refreshToken);
  };

  const clearTokens = () => {
    localStorage.removeItem(accessKey);
    localStorage.removeItem(refreshKey);
  };

  let refreshing: Promise<boolean> | null = null;

  const requestNewTokens = async () => {
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
      onExpired?.();
      return false;
    }

    try {
      const tokens = await accountApi.webMixerServiceRefresh({
        body: { refreshToken }
      });

      setTokens(tokens.accessToken, tokens.refreshToken);
      return true;
    } catch (error) {
      console.error(error);
      onExpired?.();
      return false;
    }
  };

  const refreshAccessToken = async () => {
    if (refreshing) {
      return refreshing;
    }

    refreshing = requestNewTokens();

    try {
      return await refreshing;
    } finally {
      refreshing = null;
    }
  };

  return {
    getAccessToken,
    getRefreshToken,
    setTokens,
    clearTokens,
    refreshAccessToken
  };
};

export type TokenStore = ReturnType<typeof createTokenStore>;

// passport / upload-session token, used by Order・Payment・Upload APIs
export const apiTokens = createTokenStore('access_token', 'refresh_token');

// WebLogin token, used by the passport bind/list/unbind flow
export const sessionTokens = createTokenStore(
  'session_access_token',
  'session_refresh_token',
  () => {
    sessionTokens.clearTokens();
    apiTokens.clearTokens();
    void signOutFirebase();
    window.location.replace('/login');
  }
);

export const { getAccessToken, setTokens, clearTokens } = apiTokens;

export const isLoggedIn = () => Boolean(sessionTokens.getAccessToken());

const SILENT_FIREBASE_ERROR_CODES = [
  'auth/popup-closed-by-user',
  'auth/cancelled-popup-request'
];

export const isSilentFirebaseError = (error: unknown) =>
  error instanceof Error &&
  'code' in error &&
  SILENT_FIREBASE_ERROR_CODES.includes(String(error.code));

const exchangeToken = async (credential: UserCredential) => {
  const idToken = await credential.user.getIdToken();

  try {
    const { accessToken, refreshToken } =
      await accountApi.webMixerServiceWebLogin({
        body: { idToken }
      });

    sessionTokens.setTokens(accessToken, refreshToken);

    return true;
  } catch (error) {
    await signOutFirebase();
    throw error;
  }
};

// warms up the auth iframe so the Google popup opens without delay
export const preloadGoogleLogin = () => {
  getWebAuth();
};

export const loginWithGoogle = async () => {
  const credential = await getWebAuth().googleLogin();

  if (!credential) {
    return;
  }

  return exchangeToken(credential);
};

export const sendLoginLink = async (email: string) => {
  await accountApi.webMixerServiceSendLoginEmail({ body: { email } });
  localStorage.setItem(EMAIL_FOR_SIGN_IN_KEY, email);
};

const getLinkParam = (key: string) =>
  new URLSearchParams(window.location.search).get(key) ?? '';

// new links carry `key`; links sent before it was introduced carry `email` + `oobCode`
export const isLoginLink = () =>
  Boolean(getLinkParam('key') || getLinkParam('oobCode'));

export const needsEmailForLink = () =>
  !getLinkParam('key') && Boolean(getLinkParam('oobCode'));

export const clearLoginLink = () =>
  window.history.replaceState(null, '', window.location.pathname);

export const getEmailForSignIn = () =>
  getLinkParam('email') || (localStorage.getItem(EMAIL_FOR_SIGN_IN_KEY) ?? '');

export const isUnauthorizedError = (error: unknown) =>
  error instanceof WebMixerService.ResponseError &&
  error.response.status === 401;

export const loginWithLink = async (email?: string) => {
  const key = getLinkParam('key');
  const body = key ? { key } : { email, oobCode: getLinkParam('oobCode') };

  const { accessToken, refreshToken } =
    await accountApi.webMixerServiceEmailLogin({ body });

  sessionTokens.setTokens(accessToken, refreshToken);
  localStorage.removeItem(EMAIL_FOR_SIGN_IN_KEY);

  return true;
};

export const loginWithWechat = async (code: string, source: string) => {
  const { accessToken, refreshToken } =
    await accountApi.webMixerServiceWechatLogin({ body: { code, source } });

  sessionTokens.setTokens(accessToken, refreshToken);
};

export const logout = async () => {
  sessionTokens.clearTokens();
  apiTokens.clearTokens();
  resetLocale();
  await signOutFirebase();
};
